import React, { type ReactNode, useCallback } from 'react';

import { ErrorBoundary, type ErrorBoundaryProps } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { useNavBoundaryContext } from '@atlassian/navigation-error-boundary';
import type { NavigationExperienceIdEnum } from '@atlassian/navigation-perf-utils';

import { PACKAGE_PREFIX } from '../../constants';
import { useMenuStateContext, useUniversalCreateState } from '../../context';

interface GenericErrorBoundaryProps {
	/**
	 * Optional app name to help identify the app where the error occurred when context is not available.
	 */
	app?: string;
	children: ReactNode;
	fallback?: ErrorBoundaryProps['fallback'];
	/**
	 * The interaction ID to help identify the experience where the error occurred.
	 */
	interactionId?: NavigationExperienceIdEnum;
	/**
	 * The name of the component using this error boundary to help identify where errors occur.
	 */
	name: string;
	onError?: (error: Error) => void;
}

export function GenericErrorBoundary({
	children,
	name,
	interactionId = undefined,
	fallback = null,
	onError = undefined,
	app = undefined,
}: GenericErrorBoundaryProps) {
	const { product, subProduct } = useMenuStateContext();
	const { product: productV1, subProduct: subProductV1 } = useUniversalCreateState();
	const tags = {
		component: PACKAGE_PREFIX,
		subProduct: subProduct || subProductV1,
	};

	const { handleError } = useConditionalNavBoundaryContext({
		location: name,
		disableSentry: false,
		disableAnalytics: true,
		onError,
		sentryConfig: { product: app || product || productV1 },
		interactionId,
		tags,
	});

	const handleOnError = useCallback(
		(error: Error) => {
			if (fg('navx_uc_sentry_enabled')) {
				handleError(error);
			} else {
				onError;
			}
		},
		[handleError, onError],
	);

	return (
		<ErrorBoundary fallback={fallback} onError={handleOnError}>
			{children}
		</ErrorBoundary>
	);
}

const useConditionalNavBoundaryContext = functionWithFG(
	'navx_uc_sentry_enabled',
	useNavBoundaryContext,
	() => ({
		handleError: () => {},
	}),
);
